/**
 * What a piece worker actually fills is not always a punnet — cartons, bags,
 * lugs and trays all arrive through `unit_type`, and the packhouse variant used
 * to hard-code "Punnets" while its two siblings said "Units". On a sheet a
 * packhouse manager SIGNS, naming a container that was never packed is wrong on
 * the page, not merely imprecise (docs/COMMODITY-FLOW-STRATEGY.md).
 *
 * So the noun is derived from the range's own unit codes rather than assumed:
 * exactly one code in range names it; a mixed range keeps the neutral "units"
 * rather than picking one of them.
 */
export interface UnitNoun { Plural: string; plural: string; singular: string }

/** Unit codes are terse machine vocabulary (PUNNET, CARTON, BOX_500G). Only a
 *  plain alphabetic code reads as an English noun — "BOX_500Gs" does not — so
 *  anything else keeps the neutral word. */
const PLAIN_CODE = /^[A-Za-z]{2,16}$/
const NEEDS_ES   = /(s|x|z|ch|sh)$/i
/** Units of MEASURE are not containers, and pluralising them produces "Every
 *  packer's kgs for a day" on a sheet a manager signs. HARVEST_UNIT_TYPES
 *  carries KG and a packout file can too. Falls back to "units", always safe. */
const MEASURE_CODES = new Set(['KG', 'KGS', 'G', 'T', 'TON', 'TONS', 'COUNT', 'EA'])

/** A consonant then `y` takes -ies ("entry" → "entries"); a vowel then `y`
 *  keeps -ys ("tray" → "trays", "day" → "days"). */
const CONSONANT_Y = /[^aeiou]y$/i

/** The English plural of a lower-case count noun: "crate" → "crates", "box" →
 *  "boxes", "entry" → "entries". The ONE rule for the app — `unitNounFor`
 *  below uses it, and so does every "N <noun>s" caption that is not a unit
 *  code (WeekSectionHeader). A bare `${noun}s` printed "42 entrys" on every
 *  calendar week (farm audit CR-22), the way a second copy of this printed
 *  "bagses" (#2564). Irregular nouns ("person") are not handled — name the
 *  plural yourself for those. */
export function pluralOf(singular: string): string {
  if (NEEDS_ES.test(singular)) return `${singular}es`
  if (CONSONANT_Y.test(singular)) return `${singular.slice(0, -1)}ies`
  return `${singular}s`
}

export function unitNounFor(unitTypes: string[]): UnitNoun {
  const neutral: UnitNoun = { Plural: 'Units', plural: 'units', singular: 'unit' }
  if (unitTypes.length !== 1) return neutral
  const raw = unitTypes[0].trim()
  if (!PLAIN_CODE.test(raw)) return neutral
  if (MEASURE_CODES.has(raw.toUpperCase())) return neutral
  const singular = raw.toLowerCase()
  // A code that is already plural ("BAGS") is left as it is.
  const plural = singular.endsWith('s') ? singular : pluralOf(singular)
  return { Plural: plural.charAt(0).toUpperCase() + plural.slice(1), plural, singular }
}
